import { useMemo } from 'react'
import { createPortal } from 'react-dom'

import { TableColumn } from '../types'

// Dropdown Portal
interface DropdownPortalProps<T> {
  openDropdown: { rowIndex: number; columnKey: string } | null
  dropdownPosition: { top: number; left: number; width: number } | null
  displayedData: T[]
  columns: TableColumn<T>[]
  dropdownElementRef: React.RefObject<HTMLDivElement>
  setOpenDropdown: React.Dispatch<React.SetStateAction<{ rowIndex: number; columnKey: string } | null>>
}

export function DropdownPortal<T>({
  openDropdown,
  dropdownPosition,
  displayedData,
  columns,
  dropdownElementRef,
  setOpenDropdown,
}: DropdownPortalProps<T>) {
  if (!openDropdown || !dropdownPosition || !displayedData[openDropdown.rowIndex]) return null

  const column = columns.find(col => col.key === openDropdown.columnKey)
  if (!column?.dropdown) return null

  const row = displayedData[openDropdown.rowIndex]
  const currentValue = column.dropdown.getCurrentValue ? column.dropdown.getCurrentValue(row) : (row as Record<string, unknown>)[openDropdown.columnKey] as string | number

  return createPortal(
    <div
      ref={dropdownElementRef}
      className="fixed bg-white border border-gray-200 rounded-md shadow-lg z-50 min-w-[180px]"
      role="listbox"
      style={{
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        width: `${dropdownPosition.width}px`,
        zIndex: 999999,
      }}
      tabIndex={-1}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.stopPropagation()
          e.preventDefault()
        }
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {column.dropdown.options.map((option) => {
        const isSelected = option.value === currentValue
        return (
          <div
            key={String(option.value)}
            onClick={(e) => {
              e.stopPropagation()
              column.dropdown!.onSelect(row, option.value)
              setOpenDropdown(null)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation()
                e.preventDefault()
                column.dropdown!.onSelect(row, option.value)
                setOpenDropdown(null)
              }
            }}
            tabIndex={0}
            role="option"
            aria-selected={isSelected}
            className={`px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 first:rounded-t-md last:rounded-b-md ${
              isSelected
                ? 'bg-amber-50 text-amber-900'
                : 'text-gray-700'
            }`}
          >
            {column.dropdown?.renderOption ? column.dropdown.renderOption(option) : option.label}
          </div>
        )
      })}
    </div>,
    document.body
  )
}

// DatePicker Portal
interface DatePickerPortalProps<T> {
  openDatePicker: { rowIndex: number; columnKey: string } | null
  datePickerPosition: { top: number; left: number; width: number } | null
  displayedData: T[]
  columns: TableColumn<T>[]
  datePickerComponent?: React.ComponentType<Record<string, unknown>>
  datePickerElementRef: React.RefObject<HTMLDivElement>
  setOpenDatePicker: React.Dispatch<React.SetStateAction<{ rowIndex: number; columnKey: string } | null>>
}

export function DatePickerPortal<T>({
  openDatePicker,
  datePickerPosition,
  displayedData,
  columns,
  datePickerComponent,
  datePickerElementRef,
  setOpenDatePicker,
}: DatePickerPortalProps<T>) {
  if (!openDatePicker || !datePickerPosition || !displayedData[openDatePicker.rowIndex] || !datePickerComponent) return null

  const column = columns.find(col => col.key === openDatePicker.columnKey)
  if (!column?.datePicker) return null

  const row = displayedData[openDatePicker.rowIndex]
  const currentValue = column.datePicker.getCurrentValue ? column.datePicker.getCurrentValue(row) : (row as Record<string, unknown>)[openDatePicker.columnKey] as string
  const DatePickerComponent = datePickerComponent

  // Stabilize position to prevent jumps on data updates - only update if position actually changed
  const stablePosition = useMemo(() => {
    if (!datePickerPosition) return null
    return datePickerPosition
  }, [datePickerPosition?.top, datePickerPosition?.left, datePickerPosition?.width])

  return createPortal(
    <DatePickerComponent
      ref={datePickerElementRef}
      key={`${openDatePicker.rowIndex}-${openDatePicker.columnKey}-${currentValue}`}
      value={currentValue || 'N/A'}
      onChange={(date: string) => {
        if (column.datePicker) {
          column.datePicker.onDateChange(row, date === 'N/A' ? null : date)
        }
        if (!column.datePicker?.includeTime) {
          setOpenDatePicker(null)
        }
      }}
      onConfirm={(date: string) => {
        column.datePicker!.onDateChange(row, date === 'N/A' ? null : date)
        setOpenDatePicker(null)
      }}
      onClose={() => setOpenDatePicker(null)}
      position={stablePosition || datePickerPosition}
      includeTime={column.datePicker?.includeTime}
      onQuickAction={column.datePicker?.onQuickAction ? async (action: 'today' | '+3d' | '+7d' | 'clear' | 'revert') => {
        await column.datePicker!.onQuickAction!(row, action)
        // The DatePicker will automatically update via useValueEffect when currentValue changes
        // The key prop ensures the component re-renders with the new value from the updated row
      } : undefined}
      showRevert={column.datePicker?.showRevert}
    />,
    document.body
  )
}

// Tooltip Portal
interface TooltipPortalProps<T> {
  hoveredCell: { rowIndex: number; columnKey: string } | null
  tooltipPosition: { top: number; left: number } | null
  displayedData: T[]
  columns: TableColumn<T>[]
}

export function TooltipPortal<T>({
  hoveredCell,
  tooltipPosition,
  displayedData,
  columns,
}: TooltipPortalProps<T>) {
  if (!hoveredCell || !tooltipPosition || !displayedData[hoveredCell.rowIndex]) return null

  const column = columns.find(col => col.key === hoveredCell.columnKey)
  if (!column?.showTooltip) return null

  const row = displayedData[hoveredCell.rowIndex]
  const value = (row as Record<string, unknown>)[hoveredCell.columnKey]

  return createPortal(
    <div
      className="fixed z-[9999] bg-gray-900 text-white text-xs rounded py-1.5 px-2.5 whitespace-nowrap shadow-lg pointer-events-none"
      style={{
        top: `${tooltipPosition.top}px`,
        left: `${tooltipPosition.left}px`,
        transform: 'translateY(-100%)',
        maxWidth: '90vw',
        zIndex: 999999,
      }}
    >
      {String(value || '')}
      <div className="absolute left-3 top-full w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent border-t-gray-900"></div>
    </div>,
    document.body
  )
}
